import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { MouseEvent, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { actionButtonGroupStyles as sx } from './ActionButtonGroup.styles';

export type ActionButtonGroupAction = {
	key: string;
	label: string;
	icon: ReactNode;
	onClick: () => void;
	variant?: 'contained' | 'outlined';
	iconOnly?: boolean;
	hidden?: boolean;
};

type Props = {
	actions: ActionButtonGroupAction[];
	maxInline?: number;
};

const InlineAction = ({ action }: { action: ActionButtonGroupAction }) =>
	action.iconOnly ? (
		<IconButton
			sx={sx.button}
			aria-label={action.label}
			onClick={action.onClick}
			color='primary'
		>
			{action.icon}
		</IconButton>
	) : (
		<Button
			variant={action.variant ?? 'outlined'}
			sx={sx.button}
			aria-label={action.label}
			onClick={action.onClick}
			startIcon={action.icon}
		>
			{action.label}
		</Button>
	);

export const ActionButtonGroup = ({ actions, maxInline = 2 }: Props) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const visibleActions = actions.filter(action => !action.hidden);

	if (visibleActions.length === 0) {
		return null;
	}

	const hasOverflow = visibleActions.length > maxInline;
	const inlineActions = hasOverflow
		? visibleActions.slice(0, Math.max(maxInline - 1, 0))
		: visibleActions;
	const overflowActions = hasOverflow
		? visibleActions.slice(inlineActions.length)
		: [];

	const handleOpenMenu = (event: MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleSelect = (action: ActionButtonGroupAction) => {
		handleCloseMenu();
		action.onClick();
	};

	return (
		<>
			{inlineActions.map(action => (
				<InlineAction key={action.key} action={action} />
			))}

			{hasOverflow && (
				<>
					<Button
						variant='outlined'
						color='primary'
						onClick={handleOpenMenu}
						endIcon={<KeyboardArrowDownIcon />}
						sx={sx.trigger}
					>
						{t('More')}
					</Button>

					<Menu
						anchorEl={anchorEl}
						open={!!anchorEl}
						onClose={handleCloseMenu}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
					>
						{overflowActions.map(action => (
							<MenuItem
								key={action.key}
								onClick={() => handleSelect(action)}
								sx={sx.menuItem}
							>
								{action.icon}
								{action.label}
							</MenuItem>
						))}
					</Menu>
				</>
			)}
		</>
	);
};
